import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Row, Col } from 'react-bootstrap'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import { SectionTitle } from 'Components/Section/SectionTitle'
import SubmitButton from 'Components/Form/SubmitButton'
import { SubmitButtonBlock } from 'Components/Form/SubmitButton/SubmitButtonBlock'
import { Input } from 'Components/Form/Input'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Checkbox } from 'Components/Form/Checkbox'
import Note from 'Components/Note'
import ConfirmableCheckbox from 'Components/ConfirmableCheckbox/container/ConfirmableCheckbox'
import { CityMap } from 'Components/City/CityMap'
import { allowOnlyPositiveNumbers } from 'Utils/Validators/formValidation'
import { times, timezones } from '../options'
import { preventNonNumericChars } from '../utils'
import { validateCityForm } from '../../util/validators'
import {
  COUNTRY_CODE_ENUM,
  DEFAULT_DATA,
  FRANCE_COUNTRY_ID,
  FRANCE_DATA,
} from '../../util/constants'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles(() => ({
  error: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    color: 'red',
    fontSize: '0.875rem',
    lineHeight: '1.2',
    marginTop: '0.25rem',
    zIndex: 1,
  },
  relative: { position: 'relative' },
  center: { textAlign: 'center' },
}))

const CityDetails = ({
  enableEditCountry,
  isAdmin,
  countries,
  country,
  enableChangeTimezone,
  updateCity,
  defaultValues,
  isEdit,
  isCreate,
}) => {
  const classes = useStyles()

  const [values, setValues] = useState(defaultValues)
  const [errors, setErrors] = useState({})

  const {
    countryId,
    name,
    pickupInformationURL,
    cbdRadius,
    onboardingLocation,
    newcomersMissionsLimit,
    timezone,
    checkinRadius,
    status,
    recruitmentCalendarId,
    eventsCalendarId,
    onboardingUrl,
    recruitmentOnHold,
    isBaDocumentManagementEnabled,
    isOnsiteTrainingEnabled,
    // payHourly,
    teamLeaderEnabled,
    cityLocation,
    morningCheckInTime,
    lunchCheckInTime,
    afternoonCheckInTime,
    h2hCheckInTimeBefore,
    h2hCheckInTimeAfter,
    d2dCheckInTimeBefore,
    d2dCheckInTimeAfter,
    h2hBonusCheckInTimeBefore,
    h2hBonusCheckInTimeAfter,
    d2dBonusCheckInTimeBefore,
    d2dBonusCheckInTimeAfter,
  } = values

  const countryIdRef = useRef(null)
  const nameRef = useRef(null)
  const cbdRadiusRef = useRef(null)
  const timezoneRef = useRef(null)
  const recruitmentCalendarIdRef = useRef(null)
  const eventsCalendarIdRef = useRef(null)
  const onboardingUrlRef = useRef(null)
  const morningCheckInTimeRef = useRef(null)
  const lunchCheckInTimeRef = useRef(null)
  const afternoonCheckInTimeRef = useRef(null)
  const h2hBonusCheckInTimeBeforeRef = useRef(null)
  const h2hBonusCheckInTimeAfterRef = useRef(null)
  const d2dBonusCheckInTimeBeforeRef = useRef(null)
  const d2dBonusCheckInTimeAfterRef = useRef(null)

  const isDocumentManagementBetaEnabled = useMemo(() => {
    let countryCode
    if (values.countryId && countries) {
      // Create page
      const selectedCountryIdx = countries.findIndex(
        country => country.id === values.countryId,
      )
      countryCode = countries[selectedCountryIdx].code
    } else if (country) {
      // Update page
      countryCode = country.code
    } else {
      return false
    }

    const enabled = ![COUNTRY_CODE_ENUM.NL].includes(countryCode)

    return enabled
  }, [values.countryId, countries, country])

  const scrollToErrors = errors => {
    if (errors.countryId) {
      countryIdRef.current.scrollIntoView()
    } else if (errors.name) {
      nameRef.current.scrollIntoView()
    } else if (errors.cbdRadius) {
      cbdRadiusRef.current.scrollIntoView()
    } else if (errors.timezone) {
      timezoneRef.current.scrollIntoView()
    } else if (errors.recruitmentCalendarId) {
      recruitmentCalendarIdRef.current.scrollIntoView()
    } else if (errors.onboardingUrl) {
      onboardingUrlRef.current.scrollIntoView()
    } else if (errors.morningCheckInTime) {
      morningCheckInTimeRef.current.scrollIntoView()
    } else if (errors.lunchCheckInTime) {
      lunchCheckInTimeRef.current.scrollIntoView()
    } else if (errors.afternoonCheckInTime) {
      afternoonCheckInTimeRef.current.scrollIntoView()
    } else if (errors.h2hBonusCheckInTimeBefore) {
      h2hBonusCheckInTimeBeforeRef.current.scrollIntoView()
    } else if (errors.h2hBonusCheckInTimeAfter) {
      h2hBonusCheckInTimeAfterRef.current.scrollIntoView()
    } else if (errors.d2dBonusCheckInTimeBefore) {
      d2dBonusCheckInTimeBeforeRef.current.scrollIntoView()
    } else if (errors.d2dBonusCheckInTimeAfter) {
      d2dBonusCheckInTimeAfterRef.current.scrollIntoView()
    } else if (errors.eventsCalendarId) {
      eventsCalendarIdRef.current.scrollIntoView()
    }
  }

  useEffect(() => {
    if (errors) {
      scrollToErrors(errors)
    }
  }, [errors])

  const changeProp = (prop, value) => {
    if (isCreate && prop === 'countryId') {
      if (value === FRANCE_COUNTRY_ID) {
        return setValues(values => ({
          ...values,
          [prop]: value,
          ...FRANCE_DATA,
        }))
      } else {
        return setValues(values => ({
          ...values,
          [prop]: value,
          ...DEFAULT_DATA,
        }))
      }
    } else {
      return setValues(values => ({
        ...values,
        [prop]: value,
      }))
    }
  }

  const handleSaveCityDetails = () => {
    const validationResult = validateCityForm(values, isEdit)
    const validationFailed = Object.values(validationResult).some(err => !!err)
    if (validationFailed) {
      setErrors(validationResult)
      return
    }
    setErrors({})
    updateCity(values)
  }

  return (
    <SectionWrapper className="section-wrapper city-details">
      <SectionTitle className="section-title">City Details</SectionTitle>
      {enableEditCountry && isAdmin ? (
        <Row>
          <Col xs={3}>
            <div ref={countryIdRef}>
              <SelectDashboard
                label="Country"
                onChange={(e, k, v) => changeProp('countryId', v)}
                options={countries.map(o => ({
                  value: o.id,
                  label: o.name,
                }))}
                input={{
                  value: countryId,
                }}
                error={errors['countryId']}
              />
            </div>
          </Col>
        </Row>
      ) : null}
      <Row>
        {isAdmin && (
          <Col xs={3}>
            <div ref={nameRef}>
              <Input
                label="City Name"
                value={name}
                onChange={e => changeProp('name', e.target.value)}
                cypress_id="edit-city-name-input"
                error={errors['name']}
              />
            </div>
          </Col>
        )}
        <Col xs={3}>
          <Input
            label="Pickup information URL"
            value={pickupInformationURL}
            onChange={e => changeProp('pickupInformationURL', e.target.value)}
            cypress_id="edit-city-pickup-info-url-input"
          />
        </Col>
        {isAdmin && (
          <Col xs={3}>
            <div ref={cbdRadiusRef}>
              <Input
                label="Radius"
                value={String(cbdRadius || '')}
                onChange={e =>
                  changeProp(
                    'cbdRadius',
                    e.target.value == '' ? null : Number(e.target.value),
                  )
                }
                cypress_id="edit-city-radius-input"
                error={errors['cbdRadius']}
              />
            </div>
          </Col>
        )}
        <Col xs={3}>
          <Input
            label="Onboarding Location"
            value={onboardingLocation}
            onChange={e => changeProp('onboardingLocation', e.target.value)}
            cypress_id="edit-city-onboarding-location-input"
          />
        </Col>
      </Row>
      <Row>
        <Col xs={3}>
          <SelectDashboard
            label="1 week cap for 1st time users"
            onChange={(e, k, v) => {
              return changeProp('newcomersMissionsLimit', v)
            }}
            options={[
              { label: 'No Limit', value: null },
              { label: '1', value: 1 },
              { label: '2', value: 2 },
              { label: '3', value: 3 },
              { label: '4', value: 4 },
              { label: '5', value: 5 },
              { label: '6', value: 6 },
              { label: '7', value: 7 },
              { label: '8', value: 8 },
              { label: '9', value: 9 },
              { label: '10', value: 10 },
            ]}
            input={{
              value: newcomersMissionsLimit,
            }}
            cypress_id="edit-city-one-week-cap-dropdown"
          />
        </Col>
        {isAdmin && (
          <Col xs={3}>
            <div ref={timezoneRef}>
              <SelectDashboard
                label="Timezone"
                onChange={(e, k, v) => changeProp('timezone', v)}
                options={timezones}
                input={{
                  disabled: !enableChangeTimezone,
                  value: timezone,
                }}
                error={errors['timezone']}
              />
            </div>
            <br />
            <Note>
              Its important to set this correctly else the displayed mission
              times will be incorrect.
            </Note>
          </Col>
        )}
        {isAdmin && (
          <Col xs={3}>
            <Input
              label="Checkin Radius (meters)"
              value={String(checkinRadius || '')}
              onChange={e =>
                changeProp(
                  'checkinRadius',
                  e.target.value == '' ? null : Number(e.target.value),
                )
              }
              cypress_id="edit-city-checkin-radius-input"
            />
          </Col>
        )}
        {isAdmin && (
          <Col xs={3}>
            <SelectDashboard
              label="Status"
              onChange={(e, k, v) => changeProp('status', v)}
              options={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Archived', value: 'ARCHIVED' },
              ]}
              input={{
                value: status,
              }}
              cypress_id="edit-city-status-dropdown"
            />
          </Col>
        )}
      </Row>
      <Row xs={3}>
        <Col xs={3}>
          <div ref={recruitmentCalendarIdRef}>
            <Input
              label="Communication service email"
              value={recruitmentCalendarId}
              onChange={e =>
                changeProp('recruitmentCalendarId', e.target.value)
              }
              cypress_id="edit-city-Communication-service-email-input"
              error={errors['recruitmentCalendarId']}
              disabled={isEdit}
            />
          </div>
        </Col>
        <Col xs={3}>
          <div ref={eventsCalendarIdRef}>
            <Input
              label="Recruitment Calendar Integration Link"
              value={eventsCalendarId}
              onChange={e => changeProp('eventsCalendarId', e.target.value)}
              cypress_id="edit-city-recruitment-calendar-integration-link-input"
              error={errors['eventsCalendarId']}
            />
          </div>
        </Col>
        <Col xs={3}>
          <div ref={onboardingUrlRef}>
            <Input
              label="Online Meeting URL"
              value={onboardingUrl}
              onChange={e => changeProp('onboardingUrl', e.target.value)}
              cypress_id="edit-city-online-meeting-url-input"
              error={errors['onboardingUrl']}
            />
          </div>
          <br />
          <Note>Format: meet.google.com/aaa-bbbb-ccc</Note>
        </Col>
      </Row>
      <Row xs={3}>
        <Col xs={3}>
          <Checkbox
            label="Recruitment on hold"
            input={{
              value: recruitmentOnHold ? 'checked' : undefined,
              onChange: e =>
                changeProp('recruitmentOnHold', e.target.value !== 'checked'),
            }}
            cypress_id="edit-city-recruitment-on-hold-checkbox"
          />
        </Col>
        <Col xs={3}>
          <ConfirmableCheckbox
            label="Enable BA Document Management"
            value={isBaDocumentManagementEnabled}
            disabled={!isDocumentManagementBetaEnabled}
            labelColor={isDocumentManagementBetaEnabled ? null : 'gray'}
            confirmationModal={{
              title: `${
                isBaDocumentManagementEnabled ? 'Disable' : 'Enable'
              } BA Document Management`,
              body: (
                <div className={classes.center}>
                  Are you sure you want to{' '}
                  {isBaDocumentManagementEnabled ? 'disable' : 'enable'} BA
                  Document Management for {name}?
                </div>
              ),
              onConfirm: val =>
                changeProp('isBaDocumentManagementEnabled', val),
              showOnDisable: true,
            }}
          />
        </Col>
        <Col xs={3}>
          <ConfirmableCheckbox
            label="Enable Onsite Training stage in the pipeline"
            value={isOnsiteTrainingEnabled}
            confirmationModal={{
              title: 'Warning!',
              body: (
                <div className={classes.center}>
                  Are you sure you want to{' '}
                  {isOnsiteTrainingEnabled ? 'disable' : 'enable'} this stage
                  for {name}?
                </div>
              ),
              onConfirm: val => changeProp('isOnsiteTrainingEnabled', val),
              showOnDisable: true,
            }}
          />
        </Col>
      </Row>
      <Row xs={3}>
        {/* {isAdmin && (
          <Col xs={3}>
            <Checkbox
              label="Pay Hourly"
              input={{
                value: payHourly ? 'checked' : undefined,
                onChange: e =>
                  changeProp('payHourly', e.target.value !== 'checked'),
              }}
              cypress_id="edit-city-pay-hourly-checkbox"
            />
            <br />
            <Note>
              Be very careful before enabling this. Paying hourly is seen in the
              eyes of Government bodies as treating workers as employees and not
              contractors. Hourly pay will be payout ÷ 3 (surge still applies).
            </Note>
          </Col>
        )} */}
        {isAdmin && (
          <Col xs={3}>
            <ConfirmableCheckbox
              label="Team Leader Enabled"
              value={teamLeaderEnabled}
              note={`Enabling this feature changes brand ambassador dynamics
              substantially. Existing brand ambassadors will only be able to
              book missions where a team leader has booked an identical mission
              at the same time and location. You can promote brand ambassadors
              to team leaders on the brand ambassador details page.`}
              confirmationModal={{
                title: teamLeaderEnabled
                  ? 'Disable Team Leader Mode'
                  : 'Enable Team Leader (TLA) Mode',
                body: teamLeaderEnabled ? (
                  <div>
                    <div>
                      Are you sure you want to disable Team Leader mode for this
                      city?
                      <br />
                      Disabling this feature will:
                    </div>
                    <ul>
                      <li>Revert to the standard mission booking workflow.</li>
                      <li>
                        Prevent the creation of new grouped missions for TLAs.
                      </li>
                      <li>
                        Any existing TLA-specific configurations will be hidden.
                      </li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <div>
                      Activating this feature enables the city to use TLA
                      functionality.
                      <br />
                      This means:
                    </div>
                    <ul>
                      <li>
                        Missions with the same location, date, and time will be
                        automatically grouped.
                      </li>
                      <li>
                        TLAs will be able to book these grouped missions first.
                      </li>
                      <li>
                        Once a TLA books a mission, the remaining missions in
                        the group will become available for BAs.
                      </li>
                      <li>
                        TLAs will be automatically assigned to the corresponding
                        SPY missions.
                      </li>
                    </ul>
                    <div>
                      Disabling this will revert to the standard mission booking
                      workflow without TLA-specific functionalities.
                    </div>
                  </div>
                ),
                onConfirm: val => changeProp('teamLeaderEnabled', val),
                showOnDisable: true,
                submitButtonCypressId:
                  'edit-city-team-leader-enabled-submit-button',
              }}
              cypress_id="edit-city-team-leader-enabled-checkbox"
            />
          </Col>
        )}
      </Row>

      <React.Fragment>
        <SectionTitle>Location</SectionTitle>
        <Note>Drag the pin to the location of the city.</Note>
        <CityMap
          cityLocation={cityLocation}
          editCityLocation={cityLocation =>
            changeProp('cityLocation', cityLocation)
          }
        />
      </React.Fragment>
      {isAdmin && (
        <React.Fragment>
          <SectionTitle marginTop>Starting Times</SectionTitle>
          <Row>
            <Col xs={3}>
              <div ref={morningCheckInTimeRef} className={classes.relative}>
                <SelectDashboard
                  label="Morning starting time"
                  options={times}
                  onChange={(e, k, v) => changeProp('morningCheckInTime', v)}
                  input={{
                    value: morningCheckInTime,
                  }}
                  cypress_id="edit-city-morning-starting-time-dropdown"
                />
                {errors['morningCheckInTime'] && (
                  <div className={classes.error}>
                    {errors['morningCheckInTime']}
                  </div>
                )}
              </div>
            </Col>
            <Col xs={3}>
              <div ref={lunchCheckInTimeRef} className={classes.relative}>
                <SelectDashboard
                  label="Lunch starting time"
                  options={times}
                  onChange={(e, k, v) => changeProp('lunchCheckInTime', v)}
                  input={{
                    value: lunchCheckInTime,
                  }}
                  cypress_id="edit-city-lunch-starting-time-dropdown"
                />
                {errors['lunchCheckInTime'] && (
                  <div className={classes.error}>
                    {errors['lunchCheckInTime']}
                  </div>
                )}
              </div>
            </Col>
            <Col xs={3}>
              <div ref={afternoonCheckInTimeRef} className={classes.relative}>
                <SelectDashboard
                  label="Afternoon starting time"
                  options={times}
                  onChange={(e, k, v) => changeProp('afternoonCheckInTime', v)}
                  input={{
                    value: afternoonCheckInTime,
                  }}
                  cypress_id="edit-city-afternoon-starting-time-dropdown"
                />

                {errors['afternoonCheckInTime'] && (
                  <div className={classes.error}>
                    {errors['afternoonCheckInTime']}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <SectionTitle marginTop>Allowed Check-in Times</SectionTitle>
          <Row>
            <Col xs={3}>
              <Input
                label="H2H allowed check-in before"
                value={h2hCheckInTimeBefore}
                onChange={e => {
                  changeProp(
                    'h2hCheckInTimeBefore',
                    e.target.value == ''
                      ? null
                      : allowOnlyPositiveNumbers(Number(e.target.value)),
                  )
                }}
                type="number"
                min="0"
                onKeyDown={preventNonNumericChars}
                onBlur={e =>
                  !e.target.value && changeProp('h2hCheckInTimeBefore', 0)
                }
                useExternalValue
                cypress_id="h2h-allowed-check-in-before-input"
              />
            </Col>
            <Col xs={3}>
              <Input
                label="H2H allowed check-in after"
                value={h2hCheckInTimeAfter}
                onChange={e =>
                  changeProp(
                    'h2hCheckInTimeAfter',
                    e.target.value == ''
                      ? null
                      : allowOnlyPositiveNumbers(Number(e.target.value)),
                  )
                }
                type="number"
                min="0"
                onKeyDown={preventNonNumericChars}
                onBlur={e =>
                  !e.target.value && changeProp('h2hCheckInTimeAfter', 0)
                }
                useExternalValue
                cypress_id="h2h-allowed-check-in-after-input"
              />
            </Col>
            <Col xs={3}>
              <Input
                label="D2D allowed check-in before"
                value={d2dCheckInTimeBefore}
                onChange={e =>
                  changeProp(
                    'd2dCheckInTimeBefore',
                    e.target.value == ''
                      ? null
                      : allowOnlyPositiveNumbers(Number(e.target.value)),
                  )
                }
                type="number"
                min="0"
                onKeyDown={preventNonNumericChars}
                onBlur={e =>
                  !e.target.value && changeProp('d2dCheckInTimeBefore', 0)
                }
                useExternalValue
                cypress_id="d2d-allowed-check-in-before-input"
              />
            </Col>
            <Col xs={3}>
              <Input
                label="D2D allowed check-in after"
                value={d2dCheckInTimeAfter}
                onChange={e =>
                  changeProp(
                    'd2dCheckInTimeAfter',
                    e.target.value == ''
                      ? null
                      : allowOnlyPositiveNumbers(Number(e.target.value)),
                  )
                }
                type="number"
                min="0"
                onKeyDown={preventNonNumericChars}
                onBlur={e =>
                  !e.target.value && changeProp('d2dCheckInTimeAfter', 0)
                }
                useExternalValue
                cypress_id="d2d-allowed-check-in-after-input"
              />
            </Col>
          </Row>

          <SectionTitle marginTop>Bonus Check-in Times</SectionTitle>
          <Row>
            <Col xs={3}>
              <div
                ref={h2hBonusCheckInTimeBeforeRef}
                className={classes.relative}
              >
                <Input
                  label="H2H Bonus check-in before"
                  value={h2hBonusCheckInTimeBefore}
                  onChange={e =>
                    changeProp(
                      'h2hBonusCheckInTimeBefore',
                      e.target.value === ''
                        ? null
                        : allowOnlyPositiveNumbers(Number(e.target.value)),
                    )
                  }
                  type="number"
                  min="0"
                  onKeyDown={preventNonNumericChars}
                  onBlur={e =>
                    !e.target.value &&
                    changeProp('h2hBonusCheckInTimeBefore', 0)
                  }
                  useExternalValue
                  cypress_id="h2h-bonus-check-in-before-input"
                  error={errors['h2hBonusCheckInTimeBefore']}
                />
              </div>
            </Col>
            <Col xs={3}>
              <div ref={h2hBonusCheckInTimeAfterRef}>
                <Input
                  label="H2H Bonus check-in after"
                  value={h2hBonusCheckInTimeAfter}
                  onChange={e =>
                    changeProp(
                      'h2hBonusCheckInTimeAfter',
                      e.target.value == ''
                        ? null
                        : allowOnlyPositiveNumbers(Number(e.target.value)),
                    )
                  }
                  type="number"
                  min="0"
                  onKeyDown={preventNonNumericChars}
                  onBlur={e =>
                    !e.target.value && changeProp('h2hBonusCheckInTimeAfter', 0)
                  }
                  useExternalValue
                  cypress_id="h2h-bonus-check-in-after-input"
                  error={errors['h2hBonusCheckInTimeAfter']}
                />
              </div>
            </Col>
            <Col xs={3}>
              <div ref={d2dBonusCheckInTimeBeforeRef}>
                <Input
                  label="D2D Bonus check-in before"
                  value={d2dBonusCheckInTimeBefore}
                  onChange={e =>
                    changeProp(
                      'd2dBonusCheckInTimeBefore',
                      e.target.value == ''
                        ? null
                        : allowOnlyPositiveNumbers(Number(e.target.value)),
                    )
                  }
                  type="number"
                  min="0"
                  onKeyDown={preventNonNumericChars}
                  onBlur={e =>
                    !e.target.value &&
                    changeProp('d2dBonusCheckInTimeBefore', 0)
                  }
                  useExternalValue
                  cypress_id="d2d-bonus-check-in-before-input"
                  error={errors['d2dBonusCheckInTimeBefore']}
                />
              </div>
            </Col>
            <Col xs={3}>
              <div ref={d2dBonusCheckInTimeAfterRef}>
                <Input
                  label="D2D Bonus check-in after"
                  value={d2dBonusCheckInTimeAfter}
                  onChange={e =>
                    changeProp(
                      'd2dBonusCheckInTimeAfter',
                      e.target.value == ''
                        ? null
                        : allowOnlyPositiveNumbers(Number(e.target.value)),
                    )
                  }
                  type="number"
                  min="0"
                  onKeyDown={preventNonNumericChars}
                  onBlur={e =>
                    !e.target.value && changeProp('d2dBonusCheckInTimeAfter', 0)
                  }
                  useExternalValue
                  cypress_id="d2d-bonus-check-in-after-input"
                  error={errors['d2dBonusCheckInTimeAfter']}
                />
              </div>
            </Col>
          </Row>
        </React.Fragment>
      )}
      <SubmitButtonBlock float="left" marginTop>
        <SubmitButton
          value="Save"
          onClick={handleSaveCityDetails}
          cypress_id="edit-city-save-city-details-button"
        />
      </SubmitButtonBlock>
    </SectionWrapper>
  )
}

export default CityDetails
